import dayjs from 'dayjs';

import type { FavoriteAsset } from '@/entities/asset';

const STORAGE_KEY = 'cyclone-favorite-assets';
const MAX_ITEMS = 100;

export const getFavoriteAssets = (): FavoriteAsset[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const assets = JSON.parse(stored) as FavoriteAsset[];
    if (!Array.isArray(assets)) return [];

    return assets.sort((a, b) => dayjs(b.addedAt).valueOf() - dayjs(a.addedAt).valueOf());
  } catch (error) {
    console.error('Failed to load favorite assets from localStorage:', error);
    throw error;
  }
};

export const addFavoriteAsset = (asset: FavoriteAsset): void => {
  try {
    const currentAssets = getFavoriteAssets();

    const exists = currentAssets.some((item) => item.id === asset.id);
    if (exists) return;

    const updatedAssets = [asset, ...currentAssets];
    const limitedAssets = updatedAssets.slice(0, MAX_ITEMS);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedAssets));
  } catch (error) {
    console.error('Failed to save favorite asset to localStorage:', error);
    throw error;
  }
};

export const removeFavoriteAsset = (assetId: string): void => {
  try {
    const currentAssets = getFavoriteAssets();
    const filteredAssets = currentAssets.filter((item) => item.id !== assetId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredAssets));
  } catch (error) {
    console.error('Failed to remove favorite asset from localStorage:', error);
    throw error;
  }
};

export const isFavoriteAsset = (assetId: string): boolean => {
  try {
    const currentAssets = getFavoriteAssets();
    return currentAssets.some((item) => item.id === assetId);
  } catch (error) {
    console.error('Failed to check favorite asset from localStorage:', error);
    throw error;
  }
};

export const toggleFavoriteAsset = (asset: FavoriteAsset): boolean => {
  const isFavorite = isFavoriteAsset(asset.id);

  if (isFavorite) {
    removeFavoriteAsset(asset.id);
    return false;
  } else {
    addFavoriteAsset(asset);
    return true;
  }
};

export const clearFavoriteAssets = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear favorite assets from localStorage:', error);
    throw error;
  }
};
